import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Avatar, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  Card, CardContent, Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  Inventory as InventoryIcon,
  ArrowBack as BackIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types
interface Product {
  id: number;
  product_id: string;
  name: string;
  qty: number;
  price?: string;
  category?: string;
}

interface ApiResponse {
  status: string | boolean;
  message: string;
  data: Product[];
}

interface RequestItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
}

const CreateRequisitionFb: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();

  // Form states
  const [products, setProducts] = useState<Product[]>([]);
  const [requestItems, setRequestItems] = useState<RequestItem[]>([]);
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const [currentQuantity, setCurrentQuantity] = useState<number>(0);
  const [narration, setNarration] = useState<string>('');
  
  // UI states
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getBarInventoryByBranch_empty`,
        formData,
        config
      );

      console.log("Products response:", response.data);

      if (response.data.status === "true" || response.data.status === true) {
        setProducts(response.data.data || []);
      } else {
        showNotification('Failed to fetch products', 'error');
      }
    } catch (error) {
      console.error("Fetch products error:", error);
      showNotification('Error fetching products', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Add item to request
  const addRequestItem = () => {
    if (!currentProduct || !currentQuantity || currentQuantity <= 0) {
      showNotification('Please select a product and enter valid quantity', 'warning');
      return;
    }

    // Check if product already exists in request
    const existingItem = requestItems.find(item => item.product_id === currentProduct.product_id);
    if (existingItem) {
      showNotification('This product is already in your request', 'warning');
      return;
    }

    const newItem: RequestItem = {
      id: Date.now().toString(),
      product_id: currentProduct.product_id,
      product_name: currentProduct.name,
      quantity: currentQuantity,
    };

    setRequestItems([...requestItems, newItem]);
    setCurrentProduct(null);
    setCurrentQuantity(0);
  };

  // Remove item from request
  const removeRequestItem = (id: string) => {
    setRequestItems(requestItems.filter(item => item.id !== id));
  };

  // Calculate total quantity
  useEffect(() => {
    const total = requestItems.reduce((acc, item) => acc + item.quantity, 0);
    setTotalQuantity(total);
  }, [requestItems]);

  
  // Handle form submission
  const handleSubmit = async () => {
  if (requestItems.length === 0) {
    showNotification('Please add at least one item to request', 'warning');
    return;
  }

  setSubmitting(true);
  try {
    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("narration", narration || "");

    // Add each item as PHP-compatible array keys
    requestItems.forEach((item, index) => {
      formData.append(`data[${index}][product_id]`, item.product_id);
      formData.append(`data[${index}][product_name]`, item.product_name);
      formData.append(`data[${index}][quantity]`, item.quantity.toString());
    });

    const response = await axios.post(
      `${baseUrl()}BeverageSupervisors/addBarRequest`,
      formData,
      config
    );

    const isSuccess = response.data.status === true || response.data.status === "true";

    if (isSuccess) {
      showNotification('Request submitted successfully!', 'success');
      setRequestItems([]);
      setNarration('');
      setCurrentProduct(null);
      setCurrentQuantity(0);
      await fetchProducts();
    } else {
      const errorMsg = response.data.message || 'Failed to submit request';
      showNotification(errorMsg, 'error');
    }
  } catch (error: any) {
    console.error("Submission error:", error);
    showNotification('Connection error. Please try again.', 'error');
  } finally {
    setSubmitting(false);
  }
};
  useEffect(() => {
    fetchProducts();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
          
          {/* Header */}
          <Paper 
            elevation={0}
            sx={{ 
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'background.paper'
            }}
          >
            <Container maxWidth="xl">
              <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                <IconButton onClick={() => history.goBack()}>
                  <BackIcon />
                </IconButton>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  <CartIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight={600}>
                    Create Stock Request
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Request items from main inventory
                  </Typography>
                </Box>
                <Tooltip title="Refresh Products">
                  <IconButton onClick={fetchProducts} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Container>
          </Paper>

          <Container maxWidth="lg" sx={{ py: 3 }}>
            
            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography color="text.secondary" variant="body2">Available Products</Typography>
                        <Typography variant="h4">{products.length}</Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                        <InventoryIcon />
                      </Avatar>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography color="text.secondary" variant="body2">Items in Cart</Typography>
                        <Typography variant="h4">{requestItems.length}</Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                        <CartIcon />
                      </Avatar>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography color="text.secondary" variant="body2">Total Quantity</Typography>
                        <Typography variant="h4">{totalQuantity}</Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                        <AddIcon />
                      </Avatar>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Main Form */}
            <Paper sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Add Items to Request
              </Typography>
              <Divider sx={{ mb: 4 }} />

              <Grid container spacing={3} alignItems="center">
                {/* Product Selection with Autocomplete */}
                <Grid item xs={12} md={5}>
                  <Autocomplete
                    fullWidth
                    value={currentProduct}
                    onChange={(event, newValue) => setCurrentProduct(newValue)}
                    options={products}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) => option.product_id === value?.product_id}
                    loading={loading}
                    renderOption={(props, option) => (
                      <li {...props} key={option.product_id}>
                        <Typography variant="body2">{option.name}</Typography>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Product"
                        variant="outlined"
                        size="small"
                        placeholder="Type to search products..."
                      />
                    )}
                  />
                </Grid>

                {/* Quantity Input */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Quantity"
                    type="number"
                    value={currentQuantity || ""}
                    onChange={(e) => setCurrentQuantity(Number(e.target.value) || 0)}
                    inputProps={{ min: 1 }}
                    disabled={!currentProduct}
                  />
                </Grid>

                {/* Add Button */}
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addRequestItem}
                    disabled={!currentProduct || !currentQuantity}
                    sx={{ py: 1.5 }}
                  >
                    Add Item
                  </Button>
                </Grid>
              </Grid>

              {/* Narration Field */}
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Narration / Reason for Request (Optional)"
                  value={narration}
                  onChange={(e) => setNarration(e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Add any notes or reason for this request..."
                />
              </Box>

              {/* Request Items Table */}
              {requestItems.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Request Items ({requestItems.length})
                  </Typography>
                  <TableContainer component={Paper} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {requestItems.map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {item.product_name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={item.quantity}
                                size="small"
                                sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => removeRequestItem(item.id)}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                          <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <Chip
                              label={totalQuantity}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Submit Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<SendIcon />}
                onClick={handleSubmit}
                disabled={submitting || requestItems.length === 0}
                sx={{ mt: 4, py: 1.5 }}
              >
                {submitting ? <CircularProgress size={24} /> : 'Submit Request'}
              </Button>
            </Paper>

            {/* Info Card */}
            <Paper sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <InfoIcon color="info" />
                <Typography variant="body2" color="text.secondary">
                  Requests will be sent to the warehouse for approval. You can track the status of your requests in the history section.
                </Typography>
              </Stack>
            </Paper>
          </Container>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Loading Overlay */}
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: theme.zIndex.drawer + 1,
              backdropFilter: 'blur(3px)'
            }}
            open={submitting || loading}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress color="inherit" />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {submitting ? 'Submitting request...' : 'Loading...'}
              </Typography>
            </Stack>
          </Backdrop>

        </Box>
      </IonContent>
    </IonPage>
  );
};

export default CreateRequisitionFb;
